import React, { useRef } from 'react';
import styled from 'styled-components';
import palette from '../../lib/styles/palette';
import HomePreview from './HomePreview';
import { FiX } from 'react-icons/fi';

const SettingWrap = styled.div`
    width: 100%;
    padding-top: 16px;
    display: flex;
    gap: 30px;
`;

const LeftSection = styled.div`
    flex: 1;
`;

const RightSection = styled.div`
    width: 400px;
    flex-shrink: 0;
`;

const PreviewTitle = styled.div`
    font-weight: bold;
    font-size: 1rem;
    margin-bottom: 15px;
    color: ${palette.webGray[8]};
`;

const PreviewBox = styled.div`
    border: 1px solid ${palette.webGray[16]};
    border-radius: .2rem;
    padding: 20px;
    background-color: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    position: sticky;
    top: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const SettingTitle = styled.div`
    font-weight: bold;
    font-size: 1.2rem;
    margin-bottom: 15px;
`;

const SettingBox = styled.div`
    width: 100%;
    border: 1px solid ${palette.webGray[16]};
    border-radius: .2rem;
    padding: 20px 24px;
    background-color: #fff;
    margin-bottom: 30px;
`;

const GroupTitle = styled.div`
    font-weight: bold;
    font-size: 1.1rem;
    color: ${palette.webGray[8]};
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 2px solid ${palette.webGray[16]};
`;

const GroupDescription = styled.div`
    font-size: .85rem;
    color: ${palette.webGray[6]};
    margin-top: 8px;
    line-height: 1.5;
    padding: 10px;
    background-color: #f8f9fa;
    border-radius: .2rem;
    border-left: 3px solid ${palette.webCyan[5]};
`;

const FormBox = styled.div`
    margin-bottom: 30px;
`;

const FormRow = styled.div`
    display: flex;
    align-items: ${props => props.alignTop ? 'flex-start' : 'center'};
    margin-bottom: 20px;
    
    &:last-child {
        margin-bottom: 0;
    }
`;

const FormLabel = styled.label`
    width: ${props => props.wide ? '120px' : '150px'};
    font-weight: bold;
    font-size: .95rem;
    margin-right: 20px;
    flex-shrink: 0;
`;

const FormInput = styled.input`
    flex: 1;
    border: 1px solid #cccccc;
    border-radius: .2rem;
    padding: 8px 12px;
    font-size: .95rem;
    
    &:focus {
        outline: none;
        border-color: ${palette.webCyan[5]};
    }
`;

const FormTextarea = styled.textarea`
    width: 100%;
    border: 1px solid #cccccc;
    border-radius: .2rem;
    padding: 8px 12px;
    font-size: .95rem;
    min-height: 80px;
    max-height: 80px;
    resize: none;
    line-height: 1.5;
    
    &:focus {
        outline: none;
        border-color: ${palette.webCyan[5]};
    }
`;

const TitleTextarea = styled.textarea`
    width: 100%;
    border: 1px solid #cccccc;
    border-radius: .2rem;
    padding: 8px 12px;
    font-size: .95rem;
    min-height: 60px;
    max-height: 60px;
    resize: none;
    line-height: 1.5;
    
    &:focus {
        outline: none;
        border-color: ${palette.webCyan[5]};
    }
`;

const ImageUploadBox = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const ImagePreviewBox = styled.div`
    position: relative;
    width: 100%;
    max-width: 200px;
    border: 1px solid #cccccc;
    border-radius: .2rem;
    overflow: hidden;
    background-color: #f8f8f8;
`;

const ImagePreview = styled.img`
    width: 100%;
    height: auto;
    display: block;
    max-height: 150px;
    object-fit: cover;
`;

const DeleteImageButton = styled.button`
    position: absolute;
    top: 8px;
    right: 8px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.6);
    color: #fff;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    
    &:hover {
        background-color: rgba(0, 0, 0, 0.8);
    }
    
    &:focus {
        outline: none;
    }
    
    svg {
        font-size: 16px;
    }
`;

const FileInput = styled.input`
    display: none;
`;

const UploadButton = styled.button`
    background-color: ${palette.webCyan[5]};
    color: #fff;
    border: 0;
    border-radius: .2rem;
    padding: 8px 16px;
    font-size: .9rem;
    font-weight: 500;
    cursor: pointer;
    width: fit-content;
    
    &:focus {
        outline: none;
    }
    
    &:hover {
        opacity: 0.9;
    }
    
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const FormNumberInput = styled.input`
    width: 100px;
    border: 1px solid #cccccc;
    border-radius: .2rem;
    padding: 8px 12px;
    font-size: .95rem;
    margin-right: 10px;
    
    &:focus {
        outline: none;
        border-color: ${palette.webCyan[5]};
    }
`;

const SaveButton = styled.button`
    background-color: ${palette.webCyan[5]};
    color: #fff;
    border: 0;
    border-radius: .2rem;
    padding: 10px 30px;
    font-size: 1rem;
    font-weight: bold;
    cursor: pointer;
    margin-top: 20px;
    
    &:focus {
        outline: none;
    }
    
    &:hover {
        opacity: 0.9;
    }
    
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const KioskSetting = ({
    title,
    setTitle,
    titleSize,
    setTitleSize,
    memo,
    setMemo,
    memoSize,
    setMemoSize,
    imageURL,
    setImageURL,
    phone,
    setPhone,
    password,
    setPassword,
    onModifyKioskInfo,
    onUploadImage,
    onDeleteImage,
    imageUploading,
    loading,
    clubID,
}) => {
    const fileInputRef = useRef(null);
    
    const handleImageClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };
    
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            onUploadImage(file);
        }
        // 같은 파일을 다시 선택할 수 있도록 초기화
        e.target.value = '';
    };
    return (
        <SettingWrap>
            <LeftSection>
                <SettingTitle>키오스크 설정</SettingTitle>
                
                {/* 키오스크 설정 그룹 */}
                <SettingBox>
                    <GroupTitle>키오스크 시작화면 꾸미기</GroupTitle>
                    <FormBox>
                        <FormRow alignTop>
                            <FormLabel wide>클럽 이름</FormLabel>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <TitleTextarea
                                    value={title}
                                    onChange={(e) => {
                                        const lines = e.target.value.split('\n');
                                        if (lines.length <= 2) {
                                            setTitle(e.target.value);
                                        }
                                    }}
                                    placeholder="클럽 이름을 입력하세요 (최대 2줄)"
                                    rows={2}
                                />
                                <div style={{ fontSize: '.8rem', color: '#888', marginTop: '5px' }}>
                                    최대 2줄까지 입력 가능합니다.
                                </div>
                            </div>
                        </FormRow>
                        <FormRow>
                            <FormLabel>클럽이름 글자 크기</FormLabel>
                            <FormNumberInput
                                type="number"
                                value={titleSize}
                                onChange={(e) => setTitleSize(parseInt(e.target.value) || 150)}
                                min="10"
                                max="500"
                            />
                            <span>px</span>
                        </FormRow>
                        <FormRow alignTop>
                            <FormLabel wide>설명</FormLabel>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <FormTextarea
                                    value={memo}
                                    onChange={(e) => {
                                        const lines = e.target.value.split('\n');
                                        if (lines.length <= 3) {
                                            setMemo(e.target.value);
                                        }
                                    }}
                                    placeholder="센터의 간략한 설명을 입력하세요 (최대 3줄)"
                                    rows={3}
                                />
                                <div style={{ fontSize: '.8rem', color: '#888', marginTop: '5px' }}>
                                    최대 3줄까지 입력 가능합니다.
                                </div>
                            </div>
                        </FormRow>
                        <FormRow>
                            <FormLabel>설명 글자 크기</FormLabel>
                            <FormNumberInput
                                type="number"
                                value={memoSize}
                                onChange={(e) => setMemoSize(parseInt(e.target.value) || 48)}
                                min="10"
                                max="200"
                            />
                            <span>px</span>
                        </FormRow>
                        <FormRow>
                            <FormLabel>배경 이미지</FormLabel>
                            <ImageUploadBox>
                                <FileInput
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                />
                                {imageURL ? (
                                    <ImagePreviewBox>
                                        <ImagePreview 
                                            src={imageURL.startsWith('http') 
                                                ? imageURL 
                                                : imageURL.startsWith('clubImage/')
                                                    ? `https://kr.object.ncloudstorage.com/qnoss/clubdata/${imageURL}`
                                                    : `https://kr.object.ncloudstorage.com/qnoss/clubdata/clubImage/${imageURL}`}
                                            alt="배경 이미지 미리보기"
                                        />
                                        <DeleteImageButton onClick={onDeleteImage}>
                                            <FiX />
                                        </DeleteImageButton>
                                    </ImagePreviewBox>
                                ) : (
                                    <UploadButton 
                                        onClick={handleImageClick}
                                        disabled={imageUploading}
                                    >
                                        {imageUploading ? '업로드 중...' : '이미지 업로드'}
                                    </UploadButton>
                                )}
                            </ImageUploadBox>
                        </FormRow>
                        <FormRow>
                            <FormLabel>하단 텍스트</FormLabel>
                            <FormInput
                                type="text"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="문의 000-0000-0000"
                            />
                        </FormRow>
                    </FormBox>
                </SettingBox>

                {/* 기타 그룹 */}
                <SettingBox>
                    <GroupTitle>기타</GroupTitle>
                    <FormBox>
                        <FormRow>
                            <FormLabel>비밀번호</FormLabel>
                            <div style={{ flex: 1 }}>
                                <FormInput
                                    type="text"
                                    value={password}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/[^0-9]/g, '');
                                        setPassword(value);
                                    }}
                                    placeholder="비밀번호를 입력하세요 (숫자만)"
                                />
                                <GroupDescription>
                                    <strong>관리자 모드 비밀번호</strong><br/>
                                    키오스크에서 클럽 이름을 5번 빠르게 터치하면 관리자 모드로 진입할 수 있습니다.
                                </GroupDescription>
                            </div>
                        </FormRow>
                    </FormBox>
                </SettingBox>

                <SaveButton onClick={onModifyKioskInfo} disabled={loading}>
                    {loading ? '저장 중...' : '저장'}
                </SaveButton>
            </LeftSection>
            
            <RightSection>
                <PreviewTitle>미리보기</PreviewTitle>
                <PreviewBox>
                    <HomePreview
                        title={title}
                        memo={memo}
                        imageURL={imageURL}
                        titleSize={titleSize}
                        memoSize={memoSize}
                        phone={phone}
                    />
                </PreviewBox>
            </RightSection>
        </SettingWrap>
    );
};

export default KioskSetting;

